import { ReactNode, useMemo } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { Currency } from "components/app_layout/Typography";
import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import { Table } from "lib/core_components/Table";
import { commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { TypedColumnProps } from "lib/search/SearchTable";
import { SpendInterval } from "../hooks/useSpendUrlState";
import { TrendRow } from "../spendChartRows";

const PERIOD_COLUMN_TITLE: Record<SpendInterval, string> = {
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

type Props = {
  rows: TrendRow[];
  interval: SpendInterval;
  isLoading: boolean;
  /** Rendered beside the title: the view's export button. */
  action: ReactNode;
};

/** Built outside the component: only the period column's title varies, and only by interval. */
function buildSummaryColumns(interval: SpendInterval): TypedColumnProps<TrendRow>[] {
  return [
    {
      ...commonTableColumns.titleColumn,
      title: PERIOD_COLUMN_TITLE[interval],
      key: "period",
      render: (r) => (
        <Flex alignItems="center" gap={8}>
          <PeriodBadge>
            <UIcon icon="calendar" size={16} />
          </PeriodBadge>
          <Text fontWeight="500">{r.period}</Text>
        </Flex>
      ),
    },
    {
      ...commonTableColumns.valueColumn,
      title: "Transactions",
      key: "transactions",
      render: (r) => numberFormatLong(r.transactions),
      sorter: (a, b) => a.transactions - b.transactions,
      sizeConfig: { large: 200, small: 140, medium: 160, xlarge: 240 },
    },
    {
      ...commonTableColumns.valueColumn,
      title: "Total value",
      key: "total",
      render: (r) => (r.total === 0 ? "--" : <Currency value={r.total} />),
      sorter: (a, b) => a.total - b.total,
      sizeConfig: { large: 200, small: 140, medium: 160, xlarge: 240 },
    },
  ];
}

/** The period-by-period totals behind the trends chart: the same rows the chart plots. */
export function SpendSummaryTable({ rows, interval, isLoading, action }: Props) {
  const columns = useMemo(() => buildSummaryColumns(interval), [interval]);

  return (
    <Flex column gap={16}>
      <Flex justifyContent="space-between" alignItems="center" gap={16}>
        <Text h3 color="sysTextSecondary">
          Summary table
        </Text>
        {action}
      </Flex>
      <Table<TrendRow>
        ariaLabel="Spend summary table"
        columns={columns}
        dataSource={rows}
        rowKey={(r) => r.periodStart}
        loading={isLoading}
        pagination={false}
        isError={false}
      />
    </Flex>
  );
}

const PeriodBadge = styled.span(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  borderRadius: 4,
  backgroundColor: theme.colors.sysBackgroundAlternative,
  color: theme.colors.sysTextSecondary,
  flexShrink: 0,
}));
